import { CSSResultGroup, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { OutlineElement } from '../../base/outline-element/outline-element';
import '../sic-icon/sic-icon';
import componentStyles from './sic-dropdown-list.css.lit';
import { classMap } from 'lit/directives/class-map.js';

type FocusMoveKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

/**
 * The Sic Dropdown List component
 * @element sic-dropdown-list
 * @extends OutlineElement
 *
 * @prop {Boolean} active - Whether the panel is active/open.
 * @prop {String} id - Used to provide a unique ID.
 *
 * @slot button-label: The title text for the panel component.
 * @slot dropdown-list-menu: The main panel content, visible when the panel is open.
 * @slot button-initial-value: The initial/default value for the dropdown menu.
 *
 * Light DOM styles in sic-dropdown-list.lightdom.css included in /shared.css
 */

@customElement('sic-dropdown-list')
export class SicDropdownList extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Bound handlers so removeEventListener works correctly.
   */
  private boundCloseOnEsc = (event: KeyboardEvent) => this.closeOnEsc(event);
  private boundMenuKeydown = (event: KeyboardEvent) => this.handleMenuKeydown(event);

  private focusMoves: Record<FocusMoveKey, (currentIndex: number, length: number) => number> = {
    ArrowDown: (i, len) => (i + 1) % len,
    ArrowUp: (i, len) => (i - 1 + len) % len,
    Home: () => 0,
    End: (_i, len) => len - 1,
  };

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('mousedown', this.handleMouseDown);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('keydown', this.boundMenuKeydown);
    window.addEventListener('keydown', this.boundCloseOnEsc);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('mousedown', this.handleMouseDown);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('keydown', this.boundMenuKeydown);
    window.removeEventListener('keydown', this.boundCloseOnEsc);
  }

  firstUpdated() {
    this.addRoles();
    this.addAriaLabeledby();
  }

  /**
   * Add roles to ul and menu links for accessibility.
   * https://www.w3.org/WAI/ARIA/apg/patterns/menu-button/examples/menu-button-links/
   */
  addRoles() {
    const menus = this.querySelectorAll('ul');

    menus.forEach(ul => {
      const items = ul.querySelectorAll('li');

      if (!ul.hasAttribute('role')) {
        ul.setAttribute('role', 'menu');
      }

      items.forEach(li => {
        const link = li.querySelector('a');

        // li should be role="none" for menu pattern
        li.setAttribute('role', 'none');

        if (link && !link.hasAttribute('role')) {
          link.setAttribute('role', 'menuitem');
        }
      });
    });
  }

  /**
   * Add aria-labelledby and id to menu for accessibility
   * since it is slotted content not part of shadow DOM.
   */
  addAriaLabeledby() {
    const menus = this.querySelectorAll('ul');

    menus.forEach(ul => {
      ul.setAttribute('aria-labelledby', `${this.id}-label`);
      ul.setAttribute('id', `${this.id}-panel`);
    });
  }

  /**
   * Returns all menu items in the light DOM.
   */
  getMenuItems(): HTMLAnchorElement[] {
    return Array.from(
      this.querySelectorAll<HTMLAnchorElement>('a[role="menuitem"]')
    );
  }

  /**
   * Returns the shadow DOM button.
   */
  getButtonElement(): HTMLButtonElement | null {
    return this.renderRoot?.querySelector(
      `#${this.id}-button`
    ) as HTMLButtonElement | null;
  }

  /**
   * Check if mousedown happens on the sic-dropdown-list.
   * This includes internal scroll bars and prevents focusout from firing
   * when clicking on scroll bars.
   */
  handleMouseDown(event: MouseEvent) {
    if (!(event.target instanceof HTMLElement)) return;

    if (
      event.target.localName &&
      event.target.localName === 'sic-dropdown-list'
    ) {
      event.preventDefault();
    }
  }

  /**
   * Close when focus leaves the component entirely.
   */
  handleFocusOut(event: FocusEvent) {
    const focusElement = event.relatedTarget as HTMLElement | null;

    if (focusElement && !this.contains(focusElement) && !this.renderRoot.contains(focusElement)) {
      this.active = false;
    }

    // If focus is moved somewhere else entirely (relatedTarget can be null in some cases)
    if (!focusElement) {
      this.active = false;
    }
  }

  /**
   * Allow keyboard users to open the dropdown using Enter, Space,
   * ArrowDown, or ArrowUp per WCAG menu button pattern.
   */
  handleButtonKeydown(event: KeyboardEvent) {
    const key = event.key;
    const openKeys = ['ArrowDown', 'ArrowUp', 'Enter', ' '];

    // If menu is open and user Shift+Tabs away from button, close it
    if (key === 'Tab' && event.shiftKey) {
      this.active = false;
      return;
    }

    if (!this.active && openKeys.includes(key)) {
      event.preventDefault();
      this.active = true;

      // Wait until DOM updates, then move focus into menu
      requestAnimationFrame(() => {
        const items = this.getMenuItems();
        if (!items.length) return;

        const index = key === 'ArrowUp' ? items.length - 1 : 0;
        items[index].focus();
      });
    }

    // Optional: allow Escape on button to close if open
    if (this.active && key === 'Escape') {
      event.preventDefault();
      this.active = false;
      this.getButtonElement()?.focus();
    }
  }

  /**
   * Keyboard navigation for menu items.
   */
  handleMenuKeydown(event: KeyboardEvent) {
    if (!this.active) return;

    const items = this.getMenuItems();
    if (!items.length) return;

    const target = event.target as HTMLElement;
    const currentIndex = items.findIndex(item => item === target);

    // Only handle arrow navigation when focus is on a menu item
    if (currentIndex === -1) return;

    const key = event.key;

    if (key in this.focusMoves) {
      event.preventDefault();
      const nextIndex = this.focusMoves[key as FocusMoveKey](currentIndex, items.length);
      items[nextIndex].focus();
      return;
    }

    if (key === 'Escape') {
      event.preventDefault();
      this.active = false;
      this.getButtonElement()?.focus();
      return;
    }

    if (key === 'Tab') {
      // Let normal tabbing continue, but close the menu
      this.active = false;
    }
  }

  /**
   * Closes the dropdown on Escape from anywhere.
   */
  closeOnEsc(event: KeyboardEvent) {
    if (event.key !== 'Escape') return;

    if (this.active) {
      this.active = false;
      this.getButtonElement()?.focus();
    }
  }

  /**
   * Whether the panel is active/open.
   * Controlled by parent dropdown-list component.
   */
  @property({ type: Boolean, reflect: true, attribute: true })
  active = false;

  @property({ type: String, reflect: true, attribute: true })
  disable = 'false';

  /**
   * Used to provide a unique ID
   * For dropdown-list component and accessibility purposes.
   * Used by sic-contact-us component.
   */
  @property({ type: String, reflect: false })
  id: string = self.isSecureContext
    ? self.crypto.randomUUID()
    : Math.floor(Math.random() * 100000).toString();

  isDisabled(): boolean {
    return this.disable === 'true';
  }

  toggleActive(): void {
    if (this.isDisabled()) return;
    this.active = !this.active;

    // Click can open the menu without a keydown event, so move focus in here too.
    if (this.active) {
      requestAnimationFrame(() => {
        const items = this.getMenuItems();
        if (items.length) items[0].focus();
      });
    }
  }

  render(): TemplateResult {
    const isActive = this.active ? 'active' : null;

    const buttonClasses = {
      'dropdown-list-button': true,
      'active': this.active,
      'inactive': !this.active,
      'disabled': this.isDisabled(),
    };

    const iconClasses = {
      'dropdown-list-icon': true,
      'active': this.active,
    };

    const overlayClasses = {
      'dropdown-list-overlay': true,
      'hidden': !this.active,
    };

    return html`
      <div class="${classMap(overlayClasses)}" @click=${this.forceClose}></div>

      <div class="dropdown-list">
        <div class="dropdown-list-label" id="${this.id}-label">
          <slot name="button-label"></slot>
        </div>

        <div class="dropdown-list-panel ${isActive}">
          <div class="dropdown-list-heading">
            <button
              class="${classMap(buttonClasses)}"
              id="${this.id}-button"
              aria-disabled="${this.disable}"
              aria-expanded="${this.active}"
              aria-haspopup="menu"
              aria-controls="${this.id}-panel"
              aria-labelledby="${this.id}-label"
              @click=${() => this.toggleActive()}
              @keydown=${(e: KeyboardEvent) => this.handleButtonKeydown(e)}
              tabindex=${this.isDisabled() ? -1 : 0}
            >
              <div class="dropdown-list-button-value">
                <slot name="button-initial-value"></slot>
              </div>

              <span class="${classMap(iconClasses)}">
                ${this.setIcon()}
              </span>
            </button>
          </div>

          <div class="dropdown-list-content" ?hidden=${!this.active}>
            <div class="dropdown-list-menu">
              <slot name="dropdown-list-menu"></slot>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  setIcon() {
    if (this.active) {
      return html`
        <sic-icon
          name="chevron-up"
          size="20px"
          svg-fill="currentColor"
        ></sic-icon>
      `;
    }

    return html`
      <sic-icon
        name="chevron-down"
        size="20px"
        svg-fill="currentColor"
      ></sic-icon>
    `;
  }

  forceClose = () => {
    this.active = false;
  };
}
